import React from 'react';
import clsx from 'clsx';

import PresetGrid from './PresetGrid';

// One-click starting points for the whole order bump card: how the offer is
// arranged inside it AND how the buyer accepts it. The call to action is what
// makes a card read as an order bump at all, so a preset that only moved the
// thumbnail showed half the card.
//
// Seven archetypes a seller already knows from the wild, not every combination
// the fields below can reach. Each chip sets the lot, so switching chips never
// leaves the previous one's accept row behind; the fields underneath are where
// a seller diverges from it, and the chip stays lit through the tweaks that do
// not change what the card IS — see `freeKeys`. An eighth chip, Custom, is the
// grid's own: it lights for a card past every preset and applies nothing.
//
// Switch is the card an untouched page renders: every key it sets is the
// registry default, so a fresh page and a discarded one both light the first
// chip — see PresetGrid on how the active chip is derived.

const BASE = 'variables.elements.checkout.form.orderBumps';

const CARD_KEYS = ['mediaPosition', 'horizontalAlign', 'descriptionPosition', 'badge.position'];
const ACCEPT_KEYS = ['acceptStyle', 'acceptAppearance', 'acceptPosition', 'acceptButton.width'];
const LAYOUT_KEYS = [...CARD_KEYS, ...ACCEPT_KEYS];

// The image's box. Every preset applies both: `full` forces the picture above
// the copy whatever the placement says (see getOrderBumpLayout), so leaving it
// behind would render Bar as the Banner, and the ratio rides along so a
// preset looks like its chip — a banner that came out square would be a preset
// that lied.
const SIZE_KEY = 'image.styles.size';
const RATIO_KEY = 'image.styles.aspectRatio';

// The card's own Small default: a preset is a starting point for the copy and
// the call to action, not a claim about how big the seller's picture should be.
const THUMBNAIL = { [SIZE_KEY]: 64, [RATIO_KEY]: 'square' };

// The size is also the ONE thing that separates the Banner from a small image
// placed above, which is a card a seller can build from the fields below. It
// can't be a plain layout key — Small, Medium and Large are the seller's own
// choice and must not drop the chip — so the chips only ask whether the picture
// spans the card. The ratio stays out of it entirely.
const SPANS_THE_CARD = { [SIZE_KEY]: size => size === 'full' };
const FITS_BESIDE_THE_COPY = { [SIZE_KEY]: size => size !== 'full' };

// What a chip depicts is the arrangement and the KIND of call to action: what
// the buyer taps and what it sits on. Which corner it takes, whether a button
// fills its line, and where the description and the highlight go are the
// seller's tweaks, so every preset sets them and none of them matches on them.
const THE_SELLERS_TWEAKS = [
  'descriptionPosition',
  'badge.position',
  'acceptPosition',
  'acceptButton.width',
];

// With the copy left-aligned the title row runs to the card's edge, so the
// highlight reads as a tag on the offer at its end. Only centred copy has no
// row end to speak of.
const SIDE_BY_SIDE = {
  mediaPosition: 'left',
  horizontalAlign: 'left',
  descriptionPosition: 'column',
  'badge.position': 'inline',
};

// A control beside the offer at the top right sits in the title row, and that
// corner is where an inline highlight would go, so the highlight moves above
// the title instead of crowding it.
const CONTROL_IN_THE_CORNER = { ...SIDE_BY_SIDE, 'badge.position': 'above' };

export const PRESETS = [
  {
    // A bare switch on the offer, the way an app settings row reads: on or
    // off, nothing to press. The appearance is what keeps it apart from Row,
    // whose switch sits in a boxed row of its own.
    key: 'switch',
    name: 'Switch',
    values: {
      ...CONTROL_IN_THE_CORNER,
      acceptStyle: 'switch',
      acceptAppearance: 'offer',
      acceptPosition: 'top-right',
      'acceptButton.width': 'fit',
    },
    extras: THUMBNAIL,
    detect: FITS_BESIDE_THE_COPY,
    freeKeys: THE_SELLERS_TWEAKS,
  },
  {
    // The offer and nothing else: no control, no call to action. Tapping the
    // card takes the bump and the card's own highlight says so. No other chip
    // is None, so the selector is the one key that separates this card.
    key: 'card',
    name: 'Card',
    values: {
      ...SIDE_BY_SIDE,
      acceptStyle: 'none',
      acceptAppearance: 'offer',
      acceptPosition: 'top-right',
      'acceptButton.width': 'fit',
    },
    extras: THUMBNAIL,
    detect: FITS_BESIDE_THE_COPY,
    freeKeys: THE_SELLERS_TWEAKS,
  },
  {
    // Beside the offer at the top right a fit-to-text button takes the corner,
    // sitting in the title row like a control rather than on a line of its own.
    key: 'button',
    name: 'Button',
    values: {
      ...CONTROL_IN_THE_CORNER,
      acceptStyle: 'button',
      acceptAppearance: 'offer',
      acceptPosition: 'top-right',
      'acceptButton.width': 'fit',
    },
    extras: THUMBNAIL,
    detect: FITS_BESIDE_THE_COPY,
    freeKeys: THE_SELLERS_TWEAKS,
  },
  {
    key: 'bar',
    name: 'Bar',
    values: {
      ...SIDE_BY_SIDE,
      acceptStyle: 'mark',
      acceptAppearance: 'bar',
      acceptPosition: 'top-right',
      'acceptButton.width': 'fit',
    },
    extras: THUMBNAIL,
    detect: FITS_BESIDE_THE_COPY,
    freeKeys: THE_SELLERS_TWEAKS,
  },
  {
    key: 'row',
    name: 'Row',
    values: {
      ...SIDE_BY_SIDE,
      acceptStyle: 'switch',
      acceptAppearance: 'row',
      acceptPosition: 'top-right',
      'acceptButton.width': 'fit',
    },
    extras: THUMBNAIL,
    detect: FITS_BESIDE_THE_COPY,
    freeKeys: THE_SELLERS_TWEAKS,
  },
  {
    // The picture is the point of this card, centred over the copy on its own
    // row, so it starts a size up from the thumbnail the side-by-side presets
    // tuck beside the title.
    key: 'spotlight',
    name: 'Spotlight',
    values: {
      mediaPosition: 'top',
      horizontalAlign: 'center',
      descriptionPosition: 'column',
      'badge.position': 'above',
      acceptStyle: 'button',
      acceptAppearance: 'offer',
      acceptPosition: 'bottom-center',
      'acceptButton.width': 'full',
    },
    extras: { [SIZE_KEY]: 128, [RATIO_KEY]: 'square' },
    detect: FITS_BESIDE_THE_COPY,
    freeKeys: THE_SELLERS_TWEAKS,
  },
  {
    key: 'banner',
    name: 'Banner',
    values: {
      mediaPosition: 'top',
      horizontalAlign: 'left',
      descriptionPosition: 'column',
      'badge.position': 'inline',
      acceptStyle: 'button',
      acceptAppearance: 'offer',
      acceptPosition: 'bottom-left',
      'acceptButton.width': 'full',
    },
    extras: { [SIZE_KEY]: 'full', [RATIO_KEY]: 'wide' },
    detect: SPANS_THE_CARD,
    // A picture spanning the card settles the placement on its own — the render
    // stacks it whatever the field says — and centring the copy under it is
    // styling, not a different card. Matching on either only cost the chip its
    // highlight: reaching the banner from Spotlight left every chip dark.
    freeKeys: [...THE_SELLERS_TWEAKS, 'mediaPosition', 'horizontalAlign'],
  },
];

// Every thumb is drawn from the same parts: a 22px block for the image, an 8px
// bar for a line of copy, and one glyph for the call to action in the darkest
// grey so it is the first thing read — the way it is on the card.
const Line = ({ className = '' }) => (
  <div className={`h-2 rounded bg-gray-200 dark:bg-accent ${className}`} />
);
const Image = ({ className = '' }) => (
  <div className={`h-[22px] w-[22px] shrink-0 rounded bg-gray-300 dark:bg-accent ${className}`} />
);
const Banner = () => <div className="h-[14px] w-full rounded bg-gray-300 dark:bg-accent" />;

const Mark = () => <div className="h-2 w-2 shrink-0 rounded-sm bg-gray-400" />;
const Switch = () => <div className="h-2 w-3.5 shrink-0 rounded-full bg-gray-400" />;
const Button = ({ className = '' }) => <div className={`h-2 rounded bg-gray-400 ${className}`} />;

// A strip across the card with the control at one end and its copy beside it:
// filled, as a bar is, or boxed, as a row is.
const Strip = ({ control, boxed = false }) => (
  <div
    className={clsx('flex flex-row-reverse items-center gap-1.5 rounded px-1.5 py-1', {
      'border border-gray-300 dark:border-border': boxed,
      'bg-gray-100 dark:bg-muted': !boxed,
    })}
  >
    {control}
    <Line className="w-1/2" />
  </div>
);

// A long line over a short one, at the same 3:2 whatever it sits in.
const Copy = () => (
  <div className="flex-1 space-y-1.5">
    <Line />
    <Line className="w-2/3" />
  </div>
);

// The offer, with whatever control takes its top right corner.
const Offer = ({ control = null }) => (
  <div className="flex w-full items-start gap-1.5">
    <Image />
    <Copy />
    {control}
  </div>
);

function Thumb({ kind }) {
  switch (kind) {
    case 'switch':
      return <Offer control={<Switch />} />;
    case 'card':
      return <Offer />;
    case 'button':
      return <Offer control={<Button className="w-1/3" />} />;
    case 'bar':
      return (
        <div className="w-full space-y-1.5">
          <Strip control={<Mark />} />
          <Offer />
        </div>
      );
    case 'row':
      return (
        <div className="w-full space-y-1.5">
          <Strip control={<Switch />} boxed />
          <Offer />
        </div>
      );
    case 'spotlight':
      return (
        <div className="w-full space-y-1">
          <Image className="mx-auto" />
          <Line className="mx-auto w-1/2" />
          <Button className="w-full" />
        </div>
      );
    case 'banner':
      return (
        <div className="w-full space-y-1">
          <Banner />
          <Line className="w-2/3" />
          <Button className="w-full" />
        </div>
      );
    case 'custom':
      return (
        <div className="h-[22px] w-full rounded border border-dashed border-gray-300 dark:border-border" />
      );
    default:
      return null;
  }
}

export default function OrderBumpPresets({ values, setFieldValue }) {
  return (
    <PresetGrid
      base={BASE}
      keys={LAYOUT_KEYS}
      presets={PRESETS}
      values={values}
      setFieldValue={setFieldValue}
      renderThumb={kind => <Thumb kind={kind} />}
      customLabel="Custom"
    />
  );
}
